import React, { useState, useEffect } from "react";
import { Card, CardContent } from "../ui/card";
import { AlertCircle, Loader, ArrowRight } from "lucide-react";
import { Loader2 } from "lucide-react";
import { useAuth } from "../../hooks/useAuth";
import API_CONFIG from "../../lib/ApiConfig";

import ExtendedWarrantyScreenWrapper, {
  ExtendedWarrantyHeader,
  ExtendedWarrantyCard,
  ExtendedWarrantyButton,
} from "../extended-warranty/ExtendedWarrantyScreenWrapper";
import giftTime from "../../assets/images/gift-time.svg";
import analyticsService from "../../lib/AnalyticsService";
import { useInteractionTracking } from "../../hooks/useAnalytics";
const RefinancingOfferScreen = ({
  onAcceptOffer,
  onReject,
}) => {
  const [isProcessing, setIsProcessing] = useState(false);
  const [error, setError] = useState(null);
  const { getIdToken, user } = useAuth();
  const { trackButtonClick } = useInteractionTracking();
const [isLoading, setIsLoading] = useState(true);
  const [hasOffers, setHasOffers] = useState(false);

useEffect(() => {
    const checkOffers = async () => {
      try {
        if (!user) {
          return;
        }
        // Get user token
        const token = await getIdToken();
        if (token) {
          // Construct URL for checking offers
          const url = `${API_CONFIG.BASE_URL}/api/refinancing/offers`;

          // Fetch offers
          const response = await fetch(url, {
            method: "GET",
            headers: {
              "Content-Type": "application/json",
              "x-user-token": token,
              "Access-Control-Allow-Origin": "*",
              "Access-Control-Allow-Headers":
                "Origin, X-Requested-With, Content-Type, Accept, x-user-token",
            },
          });

          // Check if response is successful
          if (!response.ok) {
            throw new Error("Failed to fetch offers");
          }

          // Parse response
          const data = await response.json();

          // Determine if offers exist (adjust logic as per your API response structure)
          const offersExist = data && data.length > 0;

          setHasOffers(offersExist);

          setIsLoading(false);
        }
      } catch (err) {
        console.error("Error checking offers:", err);
        setError(err.message);
        setIsLoading(false);
      }
    };

    checkOffers();
  }, [getIdToken, user]);

  useEffect(() => {
    analyticsService.trackPageView("refinancing_offer", {
      provider: "Caribou",
    }, "Refinancing Page View");
  }, []);

  useEffect(() => {
    // Scroll the page to the top when this screen mounts
    window.scrollTo({ top: 0, behavior: "instant" });
  }, []);

  const handleOfferResponse = async (accepted) => {
    setIsProcessing(true);
    setError(null);

    try {
      trackButtonClick(
        accepted ? "accept-refinancing-offer" : "reject-refinancing-offer",
        {
          step_name: "refinancing_offer",
          provider: "Caribou",
        },
        "Refinancing Button Click"
      );

      if (accepted) {
        // Process the refinancing offer acceptance
        onAcceptOffer({ name: "Caribou" });
      } else {
        onReject();
      }
    } catch (err) {
      console.error("Error processing refinancing offer:", err);
      setError(err.message);
    } finally {
      setIsProcessing(false);
    }
  };

if (isLoading) {
    return (
      <div className="flex flex-col items-center justify-center h-screen space-y-4">
        <Loader2 className="h-12 w-12 animate-spin text-blue-500" />
        <p className="text-lg text-gray-600">
          Checking your refinancing options...
        </p>
      </div>
    );
  }

  return (
    <div className={`space-y-8 p-4 pt-5 pb-6`}>
      {error && (
        <div className="flex items-start gap-2 p-3 bg-red-50 text-red-700 rounded-lg text-sm mb-4">
          <AlertCircle className="w-5 h-5 text-red-500 flex-shrink-0 mt-0.5" />
          <p>{error}</p>
        </div>
      )}

      <ExtendedWarrantyHeader 
        title="YOU'VE GOT A REFINANCING OFFER FROM" 
        subtitle="" 
      />

      <ExtendedWarrantyCard variant="info">
        <div className="space-y-6">
          {/* Provider Logo */}
          <div className="flex justify-center">
            <img
              className="h-16 w-auto object-contain  bg-blend-darken"
              src="https://caribou.com/wp-content/uploads/2025/10/logo-normal.svg"
              alt="Caribou logo"
            />
          </div>

          {/* About Caribou Content */}
          <div className="space-y-4 text-left">
            <p className="text-base font-semibold text-darkPurple">
              Caribou: Simplifying Vehicle Refinancing
            </p>

            <p className="text-sm text-gray-700">
              Caribou makes refinancing your auto loan fast, easy, and transparent.
            </p>

            <div className="space-y-2">
              <p className="text-base font-semibold text-darkPurple">
                Why Choose Caribou?
              </p>
              <ul className="text-sm text-gray-700 space-y-2 list-disc pl-5">
                <li>
                  Zero impact on your credit score to check rates
                </li>
                <li>
                  100% online process with no paperwork
                </li>
                <li>
                  Potential savings of hundreds per year on your auto loan
                </li>
                <li>
                  Flexible terms and competitive rates
                </li>
              </ul>
            </div>
          </div>
        </div>
      </ExtendedWarrantyCard>

      {/* Reward Pending Card */}
      <Card className="shadow-none bg-[#CEC5FF66] border-none !mt-1 rounded-[20px] !mb-4">
        <CardContent className="p-5">
          <div className="flex items-center gap-3">
            <img src={giftTime} alt="giftTime" />
            <p className="text-base text-darkPurple">
              Your reward is pending until your profile is approved
            </p>
          </div>
        </CardContent>
      </Card>

      {/* Action Buttons */}
      <div className="space-y-3 mt-auto">
        <ExtendedWarrantyButton
          onClick={() => handleOfferResponse(true)}
          disabled={isProcessing}
          className="w-full"
        >
          {isProcessing ? (
            <>
              <Loader className="w-4 h-4 animate-spin" />
              <span>Processing...</span>
            </>
          ) : (
            <>
              Continue
              <ArrowRight className="w-4 h-4" />
            </>
          )}
        </ExtendedWarrantyButton>
      </div>
    </div>
  );
};

export default RefinancingOfferScreen;